import { BRAND_BG } from "@/lib/constants";
import type { Box, BuilderConfig, Geometry, RenderModel, RenderPath } from "./types";

/*
 * Path data extracted from the canonical brand assets:
 * public/brand-kit/proxidize-wordmark-white.svg (viewBox 0 0 400 90)
 * public/brand-kit/proxidize-icon-black.svg (viewBox 0 0 512 512)
 */

/** The wordmark's own yellow — the logo asset color, distinct from the UI YELLOW token. */
export const LOGO_YELLOW = "#FFDF10";

const GLYPH_HEX = { white: "#FFFFFF", black: BRAND_BG } as const;
const BACKGROUND_HEX = { black: BRAND_BG, white: "#FFFFFF" } as const;

/** The "o" glyph — doubles as the clear-space unit shown in the preview guides. */
export const O_GLYPH_PATH =
  "M113.446 75.15C108.475 75.15 104.181 74.025 100.565 71.775C96.9491 69.525 94.0113 66.6 92.2034 62.775C90.1695 58.95 89.2655 54.675 89.2655 50.175C89.2655 45.675 90.1695 41.175 92.2034 37.575C94.2373 33.975 96.9491 30.825 100.565 28.575C104.181 26.325 108.475 25.2 113.446 25.2C118.418 25.2 122.712 26.325 126.328 28.575C129.944 30.825 132.655 33.75 134.689 37.575C136.723 41.4 137.627 45.675 137.627 50.175C137.627 54.675 136.723 58.95 134.689 62.775C132.655 66.6 129.944 69.75 126.328 71.775C122.712 74.025 118.418 75.15 113.446 75.15ZM113.446 68.4C117.062 68.4 120.226 67.5 122.712 65.925C125.198 64.35 127.006 62.1 128.362 59.175C129.718 56.25 130.395 53.325 130.395 49.95C130.395 46.575 129.718 43.425 128.362 40.725C127.006 38.025 125.198 35.775 122.712 33.975C120.226 32.4 117.062 31.5 113.446 31.5C109.831 31.5 106.893 32.4 104.407 33.975C101.921 35.55 100.113 37.8 98.5311 40.725C97.1751 43.65 96.4972 46.575 96.4972 49.95C96.4972 53.325 97.1751 56.25 98.5311 59.175C99.887 62.1 101.695 64.125 104.407 65.925C106.893 67.5 109.831 68.4 113.446 68.4Z";

/** "proxidize" letter glyphs, including the two-path stylized "x". */
const WORDMARK_GLYPHS = [
  "M163.39 56.025L175.819 74.025H185.311L167.684 50.625L163.39 56.025Z",
  "M148.023 74.025L183.955 25.2H174.463L162.034 43.65L149.379 25.2H139.887L157.74 48.825L138.757 74.025H148.023Z",
  "M0 74.25V9.89999H29.1525C36.1582 9.89999 41.3559 11.7 45.4237 15.525C49.2655 19.35 51.5254 24.3 51.5254 30.825C51.5254 37.35 49.4915 42.3 45.4237 46.125C41.5819 49.95 35.9322 51.75 29.1525 51.75H7.23164V74.7H0V74.25ZM7.23164 44.55H28.4746C33.4463 44.55 37.5141 43.2 40 40.725C42.4859 38.25 43.8418 34.875 43.8418 30.375C43.8418 25.875 42.4859 22.725 40 20.25C37.5141 17.775 33.4463 16.425 28.4746 16.425H7.23164V44.1V44.55Z",
  "M59.887 74.25V25.65H67.3446V37.575C67.5706 36.675 68.0226 35.55 68.9266 34.2C69.6045 32.85 70.7345 31.275 72.3164 29.925C73.8983 28.575 75.4802 27.225 77.7401 26.325C80 25.425 82.4859 24.75 85.4237 24.75H85.8757V32.175H84.9718C81.3559 32.175 78.1921 33.075 75.4802 35.1C72.7684 37.125 70.9605 39.6 69.3785 42.75C67.7966 45.9 67.3446 49.275 67.3446 52.875V74.25H59.887Z",
  O_GLYPH_PATH,
  "M192.316 17.325V7.875H200.226V17.325H192.316ZM192.542 74.25V25.65H200V74.25H192.542Z",
  "M233.22 75.15C228.475 75.15 224.407 74.025 220.791 71.775C217.175 69.525 214.463 66.6 212.655 62.775C210.621 58.95 209.718 54.675 209.718 50.175C209.718 45.675 210.621 41.175 212.655 37.575C214.689 33.75 217.401 30.825 220.791 28.575C224.407 26.325 228.475 25.2 233.22 25.2C237.966 25.2 240.904 25.875 243.616 27.45C246.328 29.025 248.814 31.05 250.847 33.75V6.29999H258.305V74.475H250.847V66.6C249.04 69.3 246.78 71.325 243.616 72.9C240.678 74.25 237.288 75.15 233.22 75.15ZM234.124 68.4C237.74 68.4 240.904 67.5 243.39 65.925C245.876 64.35 247.684 62.1 249.04 59.175C250.169 56.25 251.073 53.325 251.073 49.95C251.073 46.575 250.395 43.425 249.04 40.725C247.684 38.025 245.876 35.775 243.39 33.975C240.904 32.4 237.74 31.5 234.124 31.5C230.508 31.5 227.345 32.4 224.859 33.975C222.373 35.55 220.339 37.8 218.983 40.725C217.627 43.65 216.949 46.575 216.949 49.95C216.949 53.325 217.627 56.25 218.983 59.175C220.339 62.1 222.373 64.125 224.859 65.925C227.345 67.725 230.508 68.4 234.124 68.4Z",
  "M270.508 17.325V7.875H278.418V17.325H270.508ZM270.734 74.25V25.65H278.192V74.25H270.734Z",
  "M287.458 74.25V67.275L316.836 32.175H287.91V25.425H326.102V32.175L296.723 67.275H326.102V74.025H287.458V74.25Z",
  "M357.288 75.15C352.316 75.15 348.023 74.025 344.181 71.775C340.339 69.525 337.627 66.6 335.593 62.775C333.559 58.95 332.429 54.675 332.429 50.175C332.429 45.675 333.333 41.175 335.367 37.575C337.401 33.75 340.113 30.825 343.729 28.575C347.345 26.325 351.638 25.2 356.61 25.2C361.582 25.2 365.876 26.325 369.492 28.575C373.107 30.825 375.819 33.75 377.853 37.575C379.887 41.4 380.791 45.675 380.791 50.175V53.1H339.661C340.113 56.025 341.017 58.5 342.599 60.975C344.181 63.45 345.989 65.25 348.475 66.6C350.96 67.95 353.898 68.625 357.062 68.625C360.226 68.625 363.39 67.95 365.876 66.375C368.136 64.8 369.944 62.775 371.299 60.525H379.209C377.627 64.8 374.915 68.4 371.073 71.325C367.684 73.8 362.938 75.15 357.062 75.15H357.288ZM340.113 45.9H373.333C372.655 41.85 370.847 38.25 368.136 35.325C365.424 32.4 361.582 31.05 356.836 31.05C352.09 31.05 348.475 32.4 345.537 35.325C342.599 38.25 341.017 41.625 340.113 45.9Z",
] as const;

const WORDMARK_UNDERSCORE = "M185.537 84.15H138.983V90.225H185.537V84.15Z";

/** The ® mark — an "R" glyph plus its enclosing circle stroke. */
const REGISTERED_R =
  "M390.057 6.79999C390.521 6.79999 390.872 6.80171 391.161 6.84979C391.437 6.89555 391.609 6.9769 391.738 7.10468L391.822 7.18768L391.933 7.22479C392.181 7.30727 392.343 7.426 392.5 7.58221C392.585 7.66616 392.665 7.75808 392.763 7.8703C392.841 7.95908 392.929 8.05786 393.028 8.16034C393.227 8.57667 393.399 9.12989 393.399 9.45038C393.399 9.91056 393.383 10.6066 392.867 11.1203C392.548 11.4379 392.304 11.6285 392.049 11.7668C391.86 11.8693 391.652 11.9496 391.382 12.0285C391.709 11.9025 392.024 11.6849 392.28 11.3029C392.482 11.0001 392.688 10.7242 392.82 10.4621C392.971 10.1598 393.042 9.85637 393.042 9.45038C393.042 9.13451 393.048 8.76455 392.932 8.41913C392.809 8.05072 392.567 7.75092 392.178 7.48456C391.567 6.90418 390.902 6.92498 390.057 6.92499H387.523V12.2004H390.057C390.3 12.2004 390.59 12.2032 390.89 12.1574L390.408 12.2785L390.758 12.8576L393.013 16.5998H392.463L390.032 12.5666L389.887 12.3254H387.296V16.8254H386.941V6.79999H390.057Z";
const REGISTERED_CIRCLE =
  "M390.282 21.15C395.524 21.15 399.774 16.9191 399.774 11.7C399.774 6.48091 395.524 2.25 390.282 2.25C385.04 2.25 380.791 6.48091 380.791 11.7C380.791 16.9191 385.04 21.15 390.282 21.15Z";

/** The symbol's "X" mark (two paths) in icon coordinates. */
const ICON_MARK = [
  "M261.708 248.537L317.569 329.435H360.227L281.005 224.268L261.708 248.537Z",
  "M192.642 329.435L354.133 110H311.475L255.613 192.92L198.736 110H156.078L236.316 216.178L151 329.435H192.642Z",
] as const;

const ICON_UNDERSCORE = "M360 374.435H151V401.435H360V374.435Z";

/* ── Geometry constants (measured from the path data above) ── */

/** Ink bounds of the wordmark "o" glyph — the clear-space unit. */
export const O_BOUNDS: Box = { x: 89.2655, y: 25.2, w: 48.3615, h: 49.95 };

const WORDMARK_HEIGHT = 90.225; // underscore bottom edge
const WORDMARK_WIDTH_WITH_R = 400;
const WORDMARK_WIDTH_NO_R = 380.791; // "e" / ® circle left edge

/** Ink bounds of the symbol mark (X + underscore) in icon coordinates. */
const ICON_MARK_BOX: Box = { x: 151, y: 110, w: 209.227, h: 291.435 };

/** The wordmark "x" and the icon "X" are the same shape at different scales —
 * the ratio maps the "o" clear-space unit into icon coordinates. */
const WORDMARK_X_HEIGHT = 48.825; // 74.025 − 25.2
const ICON_X_HEIGHT = 219.435; // 329.435 − 110
const ICON_O = O_BOUNDS.w * (ICON_X_HEIGHT / WORDMARK_X_HEIGHT);

export function getGeometry(config: BuilderConfig): Geometry {
  if (config.asset === "symbol") {
    const content = ICON_MARK_BOX;
    const centerX = content.x + content.w / 2;
    // Square canvas hugging the mark; padding adds exactly one "o" above and below.
    const edge = config.padding ? content.h + 2 * ICON_O : Math.max(content.w, content.h);
    const centerY = content.y + content.h / 2;
    return {
      viewBox: { x: centerX - edge / 2, y: centerY - edge / 2, w: edge, h: edge },
      content,
      o: ICON_O,
      outputW: config.size,
      outputH: config.size,
    };
  }

  const content: Box = {
    x: 0,
    y: 0,
    w: config.registered ? WORDMARK_WIDTH_WITH_R : WORDMARK_WIDTH_NO_R,
    h: WORDMARK_HEIGHT,
  };
  const o = O_BOUNDS.w;

  if (config.canvas !== "natural") {
    // Square or custom canvas: the logo sits centered, as large as the canvas
    // allows while keeping the clear space (when padding is on).
    const outputW = config.canvas === "square" ? config.size : config.customWidth;
    const outputH = config.canvas === "square" ? config.size : config.customHeight;
    const pad = config.padding ? o : 0;
    const unitsPerPx = Math.max((content.w + 2 * pad) / outputW, (content.h + 2 * pad) / outputH);
    const w = outputW * unitsPerPx;
    const h = outputH * unitsPerPx;
    return {
      viewBox: {
        x: content.x + content.w / 2 - w / 2,
        y: content.y + content.h / 2 - h / 2,
        w,
        h,
      },
      content,
      o,
      outputW,
      outputH,
    };
  }

  const viewBox: Box = config.padding
    ? { x: -o, y: -o, w: content.w + 2 * o, h: content.h + 2 * o }
    : content;
  return {
    viewBox,
    content,
    o,
    outputW: config.size,
    outputH: Math.round((config.size * viewBox.h) / viewBox.w),
  };
}

/** Single source of truth for both the live preview and the exported file. */
export function getRenderModel(config: BuilderConfig): RenderModel {
  const geometry = getGeometry(config);
  const glyph = GLYPH_HEX[config.color];
  const paths: RenderPath[] = [];

  if (config.asset === "symbol") {
    for (const d of ICON_MARK) paths.push({ d, fill: glyph });
    paths.push({ d: ICON_UNDERSCORE, fill: LOGO_YELLOW });
  } else {
    for (const d of WORDMARK_GLYPHS) paths.push({ d, fill: glyph });
    paths.push({ d: WORDMARK_UNDERSCORE, fill: LOGO_YELLOW });
    if (config.registered) {
      paths.push({ d: REGISTERED_R, fill: glyph, stroke: glyph, strokeWidth: 1 });
      paths.push({
        d: REGISTERED_CIRCLE,
        fill: "none",
        stroke: glyph,
        strokeWidth: 0.5,
        strokeMiterlimit: 10,
      });
    }
  }

  let background: RenderModel["background"] = null;
  if (config.background !== "none") {
    // Corner radius is authored in output px; convert to viewBox units and cap
    // at a full pill so oversized values stay valid.
    const radiusPx = Math.min(
      config.cornerRadius,
      Math.min(geometry.outputW, geometry.outputH) / 2
    );
    background = {
      fill: BACKGROUND_HEX[config.background],
      radius: Math.max(0, radiusPx) * (geometry.viewBox.w / geometry.outputW),
    };
  }

  return { geometry, background, paths };
}
